// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuthSession } from "../../../hooks/useAuthSession";
import { useSignInFailure } from "../../../utils/signInFailureSignal";
import { takeSignInHandoff } from "../signInHandoff";

export interface UseLoginResult {
  statusMessage: string;
  isBusy: boolean;
  isConfigured: boolean;
  handleLogin: () => Promise<void>;
}

export const SIGN_IN_UNAVAILABLE_MESSAGE =
  "Sign-in isn't available in this environment yet. Contact your Riddle administrator.";

export function useLogin(): UseLoginResult {
  const { login, isConfigured, isAuthenticated } = useAuthSession();
  const signInFailure = useSignInFailure();
  const [localMessage, setStatusMessage] = useState<string | null>(null);
  const statusMessage = !isConfigured
    ? SIGN_IN_UNAVAILABLE_MESSAGE
    : localMessage ?? signInFailure ?? "Sign in to open your workspace.";
  const [isBusy, setIsBusy] = useState(false);

  const handleLogin = useCallback(async (): Promise<void> => {
    if (!isConfigured) {
      setStatusMessage(SIGN_IN_UNAVAILABLE_MESSAGE);
      return;
    }

    setIsBusy(true);

    try {
      await login();
      setStatusMessage("Redirecting to sign-in…");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unknown sign-in error.";
      setStatusMessage(`Sign-in failed: ${message}`);
    } finally {
      setIsBusy(false);
    }
  }, [isConfigured, login]);

  const pendingHandoffRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (pendingHandoffRef.current === null) {
      pendingHandoffRef.current = takeSignInHandoff();
    }
    if (pendingHandoffRef.current && !isAuthenticated) {
      pendingHandoffRef.current = false;
      void handleLogin();
    }
  }, [handleLogin, isAuthenticated]);

  return { statusMessage, isBusy, isConfigured, handleLogin };
}
